import { useState } from "react";
import { uploadApplicationDocument } from "@/services/storageService";
import { createDocumentRecord } from "@/services/documentService";

interface FileUploadResult {
  success: boolean;
  message?: string;
  filePath: string;
  fileUrl: string;
}

export const useFileUpload = () => {
  const [isUploading, setIsUploading] = useState(false);

  const uploadFile = async (
    file: File,
    applicationId: string,
    documentType: string
  ): Promise<FileUploadResult> => {
    setIsUploading(true);

    try {
      // Use the storage service to upload the file
      const uploadResult = await uploadApplicationDocument(
        file,
        applicationId,
        documentType
      );

      if (!uploadResult.success || !uploadResult.url) {
        console.error("Error uploading file:", uploadResult.message);
        return {
          success: false,
          message: uploadResult.message || "Upload failed",
          filePath: "",
          fileUrl: "",
        };
      }

      // Create a document record in the database
      const documentResult = await createDocumentRecord(
        applicationId,
        documentType,
        uploadResult.url
      );

      if (documentResult.error) {
        console.error("Error creating document record:", documentResult.error);
        return {
          success: false,
          message: documentResult.error.message,
          filePath: documentType,
          fileUrl: uploadResult.url,
        };
      }

      return {
        success: true,
        filePath: documentType,
        fileUrl: uploadResult.url,
      };
    } catch (error) {
      console.error("Error in uploadFile:", error);
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Unknown error occurred",
        filePath: "",
        fileUrl: "",
      };
    } finally {
      setIsUploading(false);
    }
  };

  return {
    uploadFile,
    isUploading,
  };
};
